import { Box } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useTypedSelector } from '../../'
import { selectPreviousShiftDelayVMs } from '../../reducers/delayNotificationViewModel'
import { theme } from '../../Theme'
import TransparentButton from '../button/TransparentButton'
import DelayNotificationBar from '../notification/DelayNotificationBar'

// How many pending notifications are shown before the expand toggle appears
const VISIBLE_PENDING_COUNT = 2

type Props = {
  router: { push: (path: string) => void }
}

/**
 * Renders unhandled delay notifications from previous shifts, resolved by the
 * backend (GET /delayNotifications). All display decisions live in
 * selectPreviousShiftDelayVMs; this component only maps view models to markup
 * and owns the expand/collapse toggle.
 */
function PreviousShiftDelayNotifications({ router }: Props) {
  const { t } = useTranslation()
  const notifications = useTypedSelector(selectPreviousShiftDelayVMs)
  const [expanded, setExpanded] = useState(false)

  if (!notifications.length) return null

  const visible = expanded ? notifications : notifications.slice(0, VISIBLE_PENDING_COUNT)

  return (
    <div>
      <Box
        sx={{
          color: 'text.primary',
          fontFamily: theme.font.content,
          fontSize: theme.font.normal,
          fontWeight: 'bold',
          lineHeight: '1.5em',
          paddingBottom: theme.spacing.sizes.smallest,
        }}
      >
        {t('delayNotification.previousShiftDelayUnhandled')}
      </Box>
      {visible.map((vm) => (
        <Box key={vm.notificationKey} sx={{ paddingBottom: theme.spacing.sizes.smaller }}>
          <DelayNotificationBar
            notificationKey={vm.notificationKey}
            initialStep="previousShiftDelayUnhandled"
            initialText={
              vm.kind === 'deferredNearShiftEnd'
                ? t('delayNotification.deferredNearShiftEnd')
                : t('delayNotification.earlierShiftDelayDetails', vm.detail)
            }
            onAmendment={() => router.push(vm.amendmentPath)}
          />
        </Box>
      ))}
      {notifications.length > VISIBLE_PENDING_COUNT && (
        <Box sx={{ paddingBottom: theme.spacing.sizes.smaller }}>
          <TransparentButton spacing="none" onClick={() => setExpanded(!expanded)}>
            {expanded
              ? t('delayNotification.showLessPending')
              : t('delayNotification.showMorePending', { count: notifications.length })}
          </TransparentButton>
        </Box>
      )}
    </div>
  )
}

PreviousShiftDelayNotifications.displayName = 'PreviousShiftDelayNotifications'

export default PreviousShiftDelayNotifications
